/**
 * VIAssistantPreview - a static, presentational clone of the VI Research
 * Assistant welcome screen (the chatbot app's ChatWindow shell +
 * WelcomeNewHero + IntentGrid). Purely visual: nothing here is wired up.
 *
 * Colours, spacing and type are copied from the chatbot source of truth:
 *   vi-lead-assistant-monorepo/frontend/src/chatbot/styles.css      (tokens)
 *   .../components/ChatWindow.jsx                                  (shell)
 *   .../components/heroStyles.js + WelcomeNewHero.jsx               (hero)
 *   .../components/IntentGrid.jsx                                  (tiles)
 */

import React from 'react';
import {
  Search,
  Briefcase,
  Landmark,
  BarChart3,
  Users,
  Activity,
  Database,
  PanelLeft,
  User,
  Sparkles,
  MessageSquare,
  ArrowRight,
} from 'lucide-react';
import viLogo from '../../images/vi-logo.png';

const FONT = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const VI_ASSISTANT_URL = 'https://pre-trial.ventureintelligence.com/chatbot';

// Sample threads, so the sidebar reads like an account in use rather than an
// empty shell.
const THREADS = [
  { title: 'Fintech Series A deals, 2025', time: '2h ago', active: true },
  { title: 'Exit comps for D2C brands', time: 'Yesterday' },
  { title: 'Most active semiconductor investors', time: '3d ago' },
];

// Mirrors the guided-flow intents an investor persona sees, with the same
// icon mapping IntentGrid uses.
const INTENTS = [
  { icon: Search, title: 'Source new deals', desc: 'Find acquisition / investment targets' },
  { icon: Briefcase, title: 'Portfolio Monitoring', desc: "Track activity across your portfolio's sectors" },
  { icon: Landmark, title: 'Identify exit opportunities', desc: 'Find exit routes and potential acquirers' },
  { icon: BarChart3, title: 'Analyse a sector', desc: 'Analyse deal activity, investor trends and valuations in a sector' },
  { icon: Users, title: 'Benchmark a company or investor', desc: 'Benchmark a company or investor against peers' },
  { icon: Activity, title: 'Track fund activity', desc: 'Track fund activity and investor track records' },
];

const VIAssistantPreview = () => (
  <div style={s.shell}>
    <div style={s.intro}>
      <div style={s.introGlow} aria-hidden="true" />
      <div style={s.introContent}>
        <span style={s.newPill}>
          <Sparkles size={12} strokeWidth={2.5} color="#E8B97E" />
          New · AI-enabled research
        </span>
        <h3 style={s.introTitle}>Thank you for downloading our report.</h3>
        <p style={s.introBody}>
          If you'd like to explore more data related to this report, you can try our{' '}
          <strong style={s.introHighlight}>AI-enabled VI Assistant</strong>. Your first{' '}
          <strong style={s.introStrong}>2 lakh tokens</strong> are on us.
        </p>
        <a
          href={VI_ASSISTANT_URL}
          target="_blank"
          rel="noopener noreferrer"
          style={s.cta}
          className="via-cta"
        >
          Explore the VI Assistant
          <ArrowRight size={15} strokeWidth={2.5} />
        </a>
      </div>
    </div>

    <a
      href={VI_ASSISTANT_URL}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Try the AI-enabled VI Assistant"
      title="Opens the VI Assistant in a new tab"
      style={s.link}
      className="via-root"
    >
      <div style={s.previewFrame}>
        <div style={s.previewInner}>
          <div style={s.window} aria-hidden="true">
            {/* embedded so hover/placeholder/media rules survive outside the chatbot app */}
            <style>{`
              .via-root ::placeholder { color: #A0A0B4; opacity: 1; }
              .via-tile { transition: all 0.15s; }
          .via-cta { transition: transform 0.15s, box-shadow 0.15s; }
          .via-cta:hover {
            transform: translateY(-1px);
            box-shadow: 0 14px 28px -12px rgba(201,169,110,1);
          }
              .via-tile:hover {
                border-color: #C9A96E;
                transform: translateY(-2px);
                box-shadow: 0 10px 24px -16px rgba(28,28,46,0.35);
              }
              @media (max-width: 900px) {
                .via-sidebar { display: none !important; }
                .via-pad { padding-left: 5% !important; padding-right: 5% !important; }
              }
            `}</style>

            {/* -- Sidebar -- */}
            <div style={s.sidebar} className="via-sidebar">
              <div style={s.brandBlock}>
                <img src={viLogo} alt="Venture Intelligence" style={s.brandLogo} />
              </div>
              <div style={s.brandDivider} />
              <div style={s.sidebarTitle}>Conversations</div>
              <div style={s.threadList}>
                {THREADS.map((t) => (
                  <div key={t.title} style={{ ...s.threadItem, ...(t.active ? s.threadItemActive : {}) }}>
                    <span style={s.threadIcon}>
                      <MessageSquare size={13} strokeWidth={2} />
                    </span>
                    <span style={s.threadBody}>
                      <span style={s.threadTitle}>{t.title}</span>
                      <span style={s.threadTime}>{t.time}</span>
                    </span>
                    {t.active && <span style={s.threadDot} />}
                  </div>
                ))}
              </div>
              <div style={{ flex: 1 }} />
              <div style={s.sidebarPrimaryBtn}>+ New search</div>
            </div>

            {/* -- Main chat area -- */}
            <div style={s.chatArea}>
              {/* Header */}
              <div style={s.header}>
                <div style={s.headerLeft}>
                  <span style={s.sidebarToggle}>
                    <PanelLeft size={16} strokeWidth={2} />
                  </span>
                  <div style={s.avatar}>VI</div>
                  <div>
                    <div style={s.headerTitle}>VI Assistant</div>
                    <div style={s.headerSub}>Venture Intelligence</div>
                  </div>
                </div>
                <div style={s.headerRight}>
                  <div style={s.tokenPill}>
                    <span style={s.tokenLabel}>Usage</span>
                    <div style={s.tokenTrack}>
                      <div style={s.tokenFill} />
                    </div>
                    <span style={s.tokenPct}>0%</span>
                  </div>
                  <span style={s.userBtn}>
                    <User size={15} strokeWidth={2} />
                  </span>
                </div>
              </div>

              {/* Message list / hero */}
              <div style={s.messageList} className="via-pad">
                <div style={s.heroWrapper}>
                  <div style={s.heroCard}>
                    <h2 style={s.heroHeading}>Welcome to VI Research Assistant</h2>

                    <div style={s.pillWrap}>
                      <div style={s.pill}>
                        <Database size={15} strokeWidth={2} color="#C9A96E" style={{ flexShrink: 0 }} />
                        <span>
                          You're loaded with <strong style={s.strongGold}>200K tokens</strong> to explore the
                          live dataset and get your queries answered.
                        </span>
                      </div>
                    </div>

                    <p style={s.heroSubheading}>What would you like to do today?</p>

                    <div style={s.grid}>
                      {INTENTS.map(({ icon: Icon, title, desc }) => (
                        <div key={title} style={s.tile} className="via-tile">
                          <div style={s.tileIcon}>
                            <Icon size={16} strokeWidth={2} color="#C9A96E" />
                          </div>
                          <div style={s.tileTitle}>{title}</div>
                          <div style={s.tileDesc}>{desc}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Composer */}
              <div style={s.inputRow}>
                <div style={s.inputInner} className="via-pad">
                  <div style={s.input}>Select an option above to choose your intent…</div>
                  <div style={s.sendBtn}>
                    <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth="2.3">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div style={s.footer}>
                Powered by <strong style={{ color: '#C9A96E' }}>Venture Intelligence</strong>
              </div>
            </div>
          </div>
        </div>
      </div>
    </a>
  </div>
);

const s = {
  shell: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    fontFamily: FONT,
    background: '#FFFFFF',
  },

  // -- Thank-you message: the primary content of this modal --
  intro: {
    position: 'relative',
    flexShrink: 0,
    padding: '26px 28px 24px',
    textAlign: 'center',
    background: 'linear-gradient(135deg, #0a1128 0%, #1C1C2E 58%, #2E2E4A 100%)',
    overflow: 'hidden',
  },
  introGlow: {
    position: 'absolute',
    top: '-140px',
    left: '50%',
    width: '520px',
    height: '320px',
    marginLeft: '-260px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(201,169,110,0.28) 0%, rgba(201,169,110,0) 70%)',
    pointerEvents: 'none',
  },
  introContent: {
    position: 'relative',
  },
  newPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 12px',
    marginBottom: '12px',
    borderRadius: '999px',
    border: '1px solid rgba(201,169,110,0.45)',
    background: 'rgba(201,169,110,0.12)',
    color: '#E8B97E',
    fontSize: '10.5px',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  },
  introTitle: {
    margin: '0 0 8px',
    fontSize: '22px',
    fontWeight: 800,
    letterSpacing: '-0.3px',
    color: '#FFFFFF',
  },
  introBody: {
    margin: '0 auto',
    maxWidth: '620px',
    fontSize: '14px',
    lineHeight: 1.6,
    color: 'rgba(255,255,255,0.66)',
  },
  introHighlight: {
    color: '#E8B97E',
    fontWeight: 700,
    // Keeps the product name on one line instead of spilling "Assistant" over.
    whiteSpace: 'nowrap',
  },
  introStrong: {
    color: '#E8B97E',
    fontWeight: 700,
  },
  cta: {
    display: 'inline-flex',
    alignItems: 'center',
    marginTop: '18px',
    gap: '7px',
    padding: '11px 24px',
    borderRadius: '999px',
    background: 'linear-gradient(135deg, #C9A96E 0%, #E8B97E 100%)',
    color: '#1C1C2E',
    fontSize: '13.5px',
    fontWeight: 700,
    textDecoration: 'none',
    boxShadow: '0 10px 24px -12px rgba(201,169,110,0.9)',
  },

  // -- Click target: the entire preview opens the assistant --
  link: {
    display: 'block',
    width: '100%',
    textDecoration: 'none',
    color: 'inherit',
    cursor: 'pointer',
    borderRadius: '0 0 12px 12px',
    overflow: 'hidden',
  },
  // -- Scaled-down product shot of the assistant --
  previewFrame: {
    position: 'relative',
    width: '100%',
    height: '390px',
    overflow: 'hidden',
    borderTop: '1px solid #E2E0EC',
  },
  previewInner: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '133.333%',
    height: '520px',
    transform: 'scale(0.75)',
    transformOrigin: 'top left',
  },

  // -- Cloned app window --
  // Height stays under the modal's own max-h-[95vh] once the close-button row
  // is accounted for, so the composer and footer are never clipped off.
  window: {
    display: 'flex',
    width: '100%',
    height: '100%',
    fontFamily: FONT,
    background: '#F5F0E8',
    overflow: 'hidden',
    textAlign: 'left',
  },

  // -- Sidebar --
  sidebar: {
    width: '260px',
    background: '#0a1128',
    padding: '20px 16px 24px',
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  brandBlock: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '5px',
    marginBottom: '4px',
    background: '#F8F5F0',
    borderRadius: '8px',
    padding: '10px 12px',
  },
  brandLogo: {
    width: '130px',
    height: 'auto',
    display: 'block',
  },
  brandDivider: {
    height: '1px',
    background: 'rgba(255,255,255,0.1)',
    margin: '14px 0',
  },
  sidebarTitle: {
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: 'rgba(255,255,255,0.35)',
    marginBottom: '10px',
  },
  threadList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  threadItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '9px 11px',
    borderRadius: '12px',
    border: '1px solid transparent',
  },
  threadItemActive: {
    background: 'rgba(255,255,255,0.06)',
  },
  threadIcon: {
    flex: '0 0 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '26px',
    height: '26px',
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.06)',
    color: 'rgba(255,255,255,0.55)',
  },
  threadBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    minWidth: 0,
    flex: '1 1 auto',
  },
  threadTitle: {
    fontSize: '12.5px',
    fontWeight: 600,
    color: 'rgba(255,255,255,0.9)',
    lineHeight: 1.3,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
  threadTime: {
    fontSize: '10.5px',
    fontWeight: 500,
    color: 'rgba(255,255,255,0.35)',
  },
  threadDot: {
    flex: '0 0 auto',
    width: '7px',
    height: '7px',
    borderRadius: '50%',
    background: '#D4AF37',
  },
  sidebarPrimaryBtn: {
    width: '100%',
    padding: '8px',
    borderRadius: '8px',
    background: 'rgba(255,255,255,0.12)',
    color: 'white',
    fontSize: '12px',
    fontWeight: 600,
    textAlign: 'center',
  },

  // -- Chat area --
  chatArea: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    minWidth: 0,
  },

  // -- Header --
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 20px',
    background: '#FDF8F2',
    borderBottom: '1px solid rgba(28,28,46,0.08)',
    flexShrink: 0,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  sidebarToggle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '30px',
    height: '30px',
    borderRadius: '7px',
    border: '1px solid rgba(28,28,46,0.12)',
    color: 'rgba(28,28,46,0.5)',
    flexShrink: 0,
  },
  avatar: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #C9A96E 0%, #E8B97E 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '11px',
    fontWeight: 800,
    color: '#1C1C2E',
    letterSpacing: '-0.3px',
    flexShrink: 0,
  },
  headerTitle: {
    fontSize: '14px',
    fontWeight: 700,
    color: '#1C1C2E',
    letterSpacing: '-0.2px',
  },
  headerSub: {
    fontSize: '10.5px',
    color: 'rgba(28,28,46,0.45)',
    marginTop: '1px',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  tokenPill: {
    display: 'flex',
    alignItems: 'center',
    gap: '7px',
    padding: '5px 11px',
    borderRadius: '999px',
    border: '1px solid rgba(28,28,46,0.12)',
    background: '#FFFFFF',
  },
  tokenLabel: {
    fontSize: '11px',
    fontWeight: 600,
    color: 'rgba(28,28,46,0.55)',
  },
  tokenTrack: {
    width: '54px',
    height: '5px',
    borderRadius: '999px',
    background: 'rgba(28,28,46,0.1)',
    overflow: 'hidden',
  },
  tokenFill: {
    height: '100%',
    width: '0%',
    borderRadius: '999px',
    background: '#5A9E72',
  },
  tokenPct: {
    fontSize: '11.5px',
    fontWeight: 700,
    color: '#B48C1F',
  },
  userBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '30px',
    height: '30px',
    borderRadius: '50%',
    border: '1px solid rgba(28,28,46,0.12)',
    background: '#FFFFFF',
    color: 'rgba(28,28,46,0.5)',
    flexShrink: 0,
  },

  // -- Message list --
  messageList: {
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
    padding: '10px 8%',
    background: '#f0ede8',
    display: 'flex',
    flexDirection: 'column',
  },

  // -- Hero (heroStyles.js) --
  heroWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    minHeight: 0,
    padding: '0 8px',
    textAlign: 'center',
  },
  heroCard: {
    maxWidth: '620px',
    width: '100%',
  },
  heroHeading: {
    margin: '0 0 6px',
    fontSize: '18px',
    fontWeight: 800,
    color: '#1C1C2E',
  },
  heroSubheading: {
    margin: '0 0 10px',
    fontSize: '12.5px',
    lineHeight: 1.5,
    color: '#6B6B80',
  },
  strongGold: {
    color: '#B48C1F',
    fontWeight: 700,
  },
  pillWrap: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '10px',
  },
  pill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '7px 14px',
    borderRadius: '999px',
    border: '1.5px solid #EDD9AE',
    background: '#FCF3E3',
    color: '#6B6B80',
    fontSize: '12px',
    lineHeight: 1.4,
    textAlign: 'left',
  },

  // -- Intent grid --
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '10px',
    textAlign: 'left',
  },
  tile: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '5px',
    padding: '11px',
    borderRadius: '12px',
    border: '1.5px solid #EAE4D9',
    background: '#FFFFFF',
    textAlign: 'left',
  },
  tileIcon: {
    width: '26px',
    height: '26px',
    borderRadius: '50%',
    background: '#FBF0DE',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  tileTitle: {
    fontSize: '12.5px',
    fontWeight: 700,
    color: '#1C1C2E',
  },
  tileDesc: {
    fontSize: '11px',
    lineHeight: 1.35,
    color: '#8A8AA0',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },

  // -- Composer --
  inputRow: {
    display: 'flex',
    flexDirection: 'column',
    background: '#FDF8F2',
    borderTop: '1px solid #E2E0EC',
    flexShrink: 0,
  },
  inputInner: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '8px',
    padding: '10px 8%',
  },
  input: {
    flex: 1,
    minHeight: '34px',
    padding: '8px 14px',
    borderRadius: '11px',
    border: '1px solid #E2E0EC',
    background: 'white',
    fontSize: '13px',
    lineHeight: '1.4',
    color: '#A0A0B4',
  },
  sendBtn: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    background: '#1C1C2E',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },

  // -- Footer --
  footer: {
    flexShrink: 0,
    padding: '5px 20px 6px',
    textAlign: 'center',
    fontSize: '10px',
    color: '#6B6B80',
    borderTop: '1px solid rgba(28,28,46,0.05)',
    background: '#FDF8F2',
  },
};

export default VIAssistantPreview;
